import React, { useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { Share2, Loader2, Trophy, Shield, Goal } from 'lucide-react';

const TeamBlock = ({ club, isWinner }) => (
  <div className="flex-1 flex flex-col items-center gap-3 min-w-0">
    <div
      className={`w-24 h-24 rounded-xl flex items-center justify-center overflow-hidden border-2 ${
        isWinner ? 'border-[#00FF87]' : 'border-white/15'
      }`}
      style={{
        backgroundColor: club?.colors?.primary || '#0a0d15',
        boxShadow: isWinner ? '0 0 30px rgba(0,255,135,0.35)' : 'none',
      }}
    >
      {club?.logo ? (
        <img src={club.logo} alt={club?.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
      ) : (
        <Shield size={44} className="text-white/80" />
      )}
    </div>
    <p
      className={`font-heading uppercase tracking-wider text-center text-base leading-tight break-words w-full ${
        isWinner ? 'text-[#00FF87]' : 'text-white'
      }`}
    >
      {club?.name || 'TBD'}
    </p>
    {isWinner && (
      <span className="px-3 py-1 text-[10px] uppercase tracking-widest bg-[#00FF87]/10 text-[#00FF87] border border-[#00FF87]/30 rounded-full font-bold">
        Winner
      </span>
    )}
  </div>
);

export const MatchShareButton = ({ match, homeClub, awayClub, championshipName, isItalian }) => {
  const [generating, setGenerating] = useState(false);
  const cardRef = useRef(null);

  const homeWin = (match.home_score ?? 0) > (match.away_score ?? 0);
  const awayWin = (match.away_score ?? 0) > (match.home_score ?? 0);
  const goals = (match.events || []).filter((e) => e.event_type === 'goal');

  const handleShare = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await new Promise((r) => setTimeout(r, 150));
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: '#050508' });
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `risultato-${(homeClub?.name || 'home').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-vs-${(awayClub?.name || 'away').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${homeClub?.name || '?'} ${match.home_score}-${match.away_score} ${awayClub?.name || '?'}`,
          text: isItalian
            ? `⚽ ${homeClub?.name || '?'} ${match.home_score}-${match.away_score} ${awayClub?.name || '?'} — EPT Italy`
            : `⚽ ${homeClub?.name || '?'} ${match.home_score}-${match.away_score} ${awayClub?.name || '?'} — EPT Italy`,
        });
      } else {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = fileName;
        a.click();
        toast.success(isItalian ? 'Card risultato scaricata!' : 'Result card downloaded!');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error(isItalian ? 'Errore nella generazione della card' : 'Failed to generate card');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <button
        className="flex items-center gap-1 px-2 py-1 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] rounded text-[10px] uppercase tracking-wider font-bold hover:bg-[#00F0FF]/20 transition-colors disabled:opacity-50"
        onClick={handleShare}
        disabled={generating}
        title={isItalian ? 'Condividi risultato' : 'Share result'}
        data-testid={`share-result-btn-${match.match_id}`}
      >
        {generating ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
        <span className="hidden sm:inline">{isItalian ? 'Condividi' : 'Share'}</span>
      </button>

      {generating && (
        <div style={{ position: 'fixed', left: '-2000px', top: 0, pointerEvents: 'none' }} aria-hidden>
          <div
            ref={cardRef}
            style={{ width: 620 }}
            className="bg-[#050508] p-8 space-y-6 border border-white/10"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
                  <Trophy size={18} className="text-[#FFD700]" />
                </div>
                <div>
                  <p className="font-heading text-white text-sm uppercase tracking-widest">EPT Italy</p>
                  {championshipName && (
                    <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-bold">{championshipName}</p>
                  )}
                </div>
              </div>
              {match.match_date && (
                <p className="text-[11px] text-slate-400 font-mono">
                  {new Date(match.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>

            {/* Result */}
            <div className="flex items-start justify-between gap-4">
              <TeamBlock club={homeClub} isWinner={homeWin} />
              <div className="flex flex-col items-center pt-6 shrink-0">
                <span className="px-3 py-1 mb-3 text-[10px] uppercase tracking-widest bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30 rounded-full font-bold">
                  Full Time
                </span>
                <div className="font-mono text-5xl font-bold">
                  <span className={homeWin ? 'text-[#00FF87]' : awayWin ? 'text-slate-500' : 'text-white'}>
                    {match.home_score}
                  </span>
                  <span className="text-slate-600 mx-2">-</span>
                  <span className={awayWin ? 'text-[#00FF87]' : homeWin ? 'text-slate-500' : 'text-white'}>
                    {match.away_score}
                  </span>
                </div>
              </div>
              <TeamBlock club={awayClub} isWinner={awayWin} />
            </div>

            {/* Scorers */}
            {goals.length > 0 && (
              <div className="border-t border-white/10 pt-4">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 text-center">
                  {isItalian ? 'Marcatori' : 'Scorers'}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {goals.map((g, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00E676]/10 border border-[#00E676]/25 text-[#00E676] rounded text-xs"
                    >
                      <Goal size={12} />
                      {g.player_name || '?'} {g.minute}'
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-white/10 pt-3 flex items-center justify-center">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold">ept-italy.it</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
